import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";

type HookValue<T extends HTMLElement> = {
    ref: RefObject<T | null>;
    size: DOMRect | null;
};

export const useElementSize = <T extends HTMLElement = HTMLDivElement>(): HookValue<T> => {
    const ref = useRef<T>(null);

    const [size, setSize] = useState<DOMRect | null>(null);

    useEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        // Measure inside a ResizeObserver callback (delivered after layout) rather than a layout
        // effect: reading getBoundingClientRect mid-commit forces a synchronous reflow of the
        // freshly-mounted subtree, which is a large cost when many of these mount at once.
        const observer = new ResizeObserver(() => {
            setSize(element.getBoundingClientRect());
        });

        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return { ref, size };
};
